/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * STAR FIELD — Classic "flying through stars" with audio-reactive speed
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Stars fly toward the camera (from center to edges), creating a sense of motion.
 * Speed is driven by overall audio energy — louder music = faster flight.
 * Beat detection triggers a "light-speed burst" where stars streak dramatically.
 *
 * HOW IT WORKS:
 * 1. Stars exist in 3D space (x, y, z) with z being depth.
 * 2. Each frame, z decreases (star moves toward camera).
 * 3. We project 3D → 2D using perspective: screenX = x/z, screenY = y/z.
 * 4. When z reaches 0, the star has "passed" the camera and is recycled.
 * 5. Star brightness increases as it gets closer (1/z).
 * 6. On beat: z decreases faster = warp speed effect.
 */

import { Visualization, FrameData, VisualizationSetting } from '../../types';
import { BeatPulse, SmoothedValue } from '../../utils/audioUtils';

interface Star {
  x: number;    // -1 to 1 (3D space)
  y: number;    // -1 to 1
  z: number;    // 0 to 1 (depth: 1=far, 0=close/camera)
  prevX: number;
  prevY: number;
}

export class StarField implements Visualization {
  id = 'star-field';
  name = 'Star Field';
  category = 'particles' as const;
  description = 'Flying through stars — speed driven by audio energy';
  renderer = 'canvas2d' as const;

  private canvas!: HTMLCanvasElement;
  private ctx!: CanvasRenderingContext2D;
  private stars: Star[] = [];
  private beatPulse = new BeatPulse(0.88);
  private smoothSpeed = new SmoothedValue(0.1);

  private starCount = 400;
  private baseSpeed = 0.3;

  init(canvas: HTMLCanvasElement): void {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d')!;
    this.initStars();
  }

  private initStars(): void {
    this.stars = [];
    for (let i = 0; i < this.starCount; i++) {
      this.stars.push(this.createStar(true));
    }
  }

  private createStar(randomDepth = false): Star {
    return {
      x: (Math.random() - 0.5) * 2,
      y: (Math.random() - 0.5) * 2,
      z: randomDepth ? Math.random() : 1, // new stars start far away
      prevX: 0,
      prevY: 0,
    };
  }

  render(frame: FrameData): void {
    const { overallEnergy, isBeat, beatIntensity, deltaTime } = frame;
    const { width, height } = this.canvas;
    const ctx = this.ctx;
    const cx = width / 2;
    const cy = height / 2;

    this.beatPulse.update(isBeat, beatIntensity);

    // Speed is driven by audio energy + beat burst
    const speed = this.smoothSpeed.update(
      this.baseSpeed + overallEnergy * 0.8 + this.beatPulse.value * 2.5
    );

    // Black background with very slight trail for star streaks
    ctx.fillStyle = 'rgba(0, 0, 0, 0.92)';
    ctx.fillRect(0, 0, width, height);

    const dt = deltaTime / 16.67;

    for (const star of this.stars) {
      // Save previous projected position for streak line
      const prevSX = cx + (star.x / star.z) * cx;
      const prevSY = cy + (star.y / star.z) * cy;

      // Move star toward camera (decrease z)
      star.z -= speed * 0.01 * dt;

      // Recycle stars that pass the camera
      if (star.z <= 0.001) {
        Object.assign(star, this.createStar());
        continue;
      }

      // Project 3D → 2D (perspective projection)
      const sx = cx + (star.x / star.z) * cx;
      const sy = cy + (star.y / star.z) * cy;

      // Skip if off-screen
      if (sx < -20 || sx > width + 20 || sy < -20 || sy > height + 20) {
        Object.assign(star, this.createStar());
        continue;
      }

      // Brightness: closer stars are brighter (1/z), clamped to 0-1
      const brightness = Math.min(1, (1 - star.z) * 1.5);

      // Size: closer stars are larger
      const size = Math.max(0.5, (1 - star.z) * 3);

      // ── Draw star streak (line from prev to current position) ─────────
      ctx.strokeStyle = `rgba(200, 220, 255, ${brightness * 0.7})`;
      ctx.lineWidth = size * 0.6;
      ctx.beginPath();
      ctx.moveTo(prevSX, prevSY);
      ctx.lineTo(sx, sy);
      ctx.stroke();

      // ── Draw star dot ─────────────────────────────────────────────────
      ctx.beginPath();
      ctx.arc(sx, sy, size, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(220, 235, 255, ${brightness})`;
      ctx.fill();
    }
  }

  resize(width: number, height: number): void {
    this.canvas.width = width;
    this.canvas.height = height;
    this.initStars();
  }

  destroy(): void {
    this.stars = [];
  }

  getSettings(): VisualizationSetting[] {
    return [
      { key: 'starCount', label: 'Star Count', type: 'range', value: this.starCount, min: 100, max: 800, step: 50 },
      { key: 'baseSpeed', label: 'Base Speed', type: 'range', value: this.baseSpeed, min: 0.05, max: 1, step: 0.05 },
    ];
  }

  setSettings(settings: Record<string, number | string | boolean>): void {
    if (typeof settings.starCount === 'number' && settings.starCount !== this.starCount) {
      this.starCount = settings.starCount;
      this.initStars();
    }
    if (typeof settings.baseSpeed === 'number') this.baseSpeed = settings.baseSpeed;
  }
}
